import { User } from '__generated__/User';
import AuthModal from 'common/components/AuthModal';
import Modal from 'common/components/Modal';
import { DirectLoginModal, RecommendationLoginModal } from 'common/components/SquarespaceModal';
import Bugsnag from 'common/utils/Bugsnag';
import React from 'react';

export interface AuthFormProps {
    source?: string;
    showLogin?: boolean;
    signupHeading?: React.ReactNode;
    signupMessage?: React.ReactNode;
    loginHeading?: React.ReactNode;
    loginMessage?: React.ReactNode;
    isDismissable?: boolean;
    ctaColor?: string;
    ctaMessage?: string;
    showHeaderDivider?: boolean;
    email?: string;
    // FIXME
    modalComponent?: typeof Modal | typeof DirectLoginModal | typeof RecommendationLoginModal;
    postLogin?: () => Promise<void>;
}

export type AuthProviderLoginFn = (props: AuthFormProps) => Promise<boolean>;

export interface AuthProviderValue {
    login: AuthProviderLoginFn;
    user: User | null;
}

const AuthContext = React.createContext<AuthProviderValue>({
    login: () => Promise.resolve(false),
    user: null,
});

interface Props {
    children: React.ReactNode;
    user: User | null;
}

interface State {
    showAuthModal: boolean;
    resolve?: (isAuthenticated: boolean) => void;
    authFormProps?: AuthFormProps;
}

class AuthProvider extends React.Component<Props, State> {
    state: State = {
        showAuthModal: false,
    };

    render() {
        const { user } = this.props;

        return (
            <>
                <AuthContext.Provider value={{ login: this.handleLogin, user }}>
                    {this.props.children}
                </AuthContext.Provider>
                {this.state.showAuthModal && (
                    <AuthModal
                        {...this.state.authFormProps!}
                        onSuccess={this.handleAuthenticationSuccess}
                        onDismiss={this.handleDismiss}
                    />
                )}
            </>
        );
    }

    handleLogin = (authFormProps: AuthFormProps) => {
        // User is already logged in
        if (this.props.user) {
            return Promise.resolve(true);
        }

        if (this.state.showAuthModal) {
            Bugsnag.notify({ name: 'AuthProvider', message: 'Call to login multiple times' });
            return Promise.resolve(false);
        }

        return new Promise<boolean>(resolve => {
            this.setState({
                showAuthModal: true,
                resolve,
                authFormProps,
            });
        });
    };

    handleAuthenticationSuccess = () => {
        this.state.resolve!(true);
        this.setState({
            showAuthModal: false,
            resolve: undefined,
            authFormProps: undefined,
        });
    };

    handleDismiss = () => {
        this.state.resolve!(false);
        this.setState({
            showAuthModal: false,
            resolve: undefined,
            authFormProps: undefined,
        });
    };
}

const AuthConsumer = AuthContext.Consumer;

export { AuthContext, AuthConsumer, AuthProvider };
